// @flow

import * as React from "react";
import { withRouter } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import { format } from "date-fns";

import InquiryPage from "../../components/InquiryPage";
import DataFetcher from "../../components/DataFetcher";
import { FlashMessageConsumer } from "../../components/FlashMessages";
import statusErrors from "../../data/statusErrors";
import * as formStyles from "../../utils/formStyles";
import { denormalizeJsonApi } from "../../utils/denormalizeJsonApi";
import { generateInitialValues } from "../../utils/helpers";
import { OKP_PARTNER_NAME } from "../../data/partners";

import Summary from "./Summary";
import Company from "./Company";
import Investment from "./Investment";
import Financing from "./Financing";
import PersonalInfo from "./PersonalInfo";
import InformationBankAdvisor from "./InformationBankAdvisor";
import Bank from "./Bank";
import FurtherInformation from "./FurtherInformation";
import {
  COMPANY__ROLE,
  COMPANY_ROLE__ESTABLISHED_ENTREPRENEUR,
  FEDERAL_STATE,
  BANK_CODE,
  BANK_NAME,
  BANK_ADVISOR,
  REAL_ESTATE_COLD_RENT,
  REAL_ESTATE_RENTED_OUT_AREA,
  REAL_ESTATE_TAXATION,
  USE_TYPE__OWN_USE,
  USE_TYPE__OTHER,
  REAL_ESTATE_USAGE_KIND,
  REAL_ESTATE_HERITABLE_BUILDING_RIGHT_OBLIGATION,
  REAL_ESTATE_HERITABLE_BUILDING_RIGHT,
  COMPANY__NAME,
  COMPANY__STREET,
  COMPANY__FOUNDING_DATE,
  COMPANY__LOCATION,
  COMPANY__LEGAL_FORM,
  AMOUNT,
  PROJECT_START,
  COMPANY__CONTACT_PERSON_NAME,
  COMPANY__CONTACT_PERSON_GENDER,
  COMPANY__CONTACT_PERSON_PRIVACY_POLICY,
  COMPANY__CONTACT_PERSON_EMAIL,
  COMPANY__CONTACT_PERSON_PHONE_NUMBER,
  COMPANY__CONTACT_PERSON_COMMUNICATION_ONLY_VIA_BANK_ADVISOR,
  INFORMATION_ON_GRANTS,
  PURPOSE_KIND,
  FINANCING_PRODUCT,
  FINANCING_DESCRIPTION,
  FINANCING_AMOUNT,
  INVESTMENT_AMOUNT,
  IS_ALREADY_SELF_EMPLOYED,
  IS_BANKRUPT,
  IS_SME,
  HAS_SHARES_IN_OTHER_COMPANY,
  COLLATERALS,
  REMARK,
  UWD_CHAMBER,
  UWD_CHAMBER_EXPERT,
  UWD_CHAMBER_PROJECT
} from "./formFields";

import initialResponse from "./initialResponse";

import Creator from "./Creator";
import OkpCompanyInfo from "./OkpCompanyInfo";
import OkpProjectInfo from "./OkpProjectInfo";
import OkpContactPersonInfo from "./OkpContactPersonInfo";
import OkpGrantsInfo from "./OkpGrantsInfo";
import OkpFinancingInfo from "./OkpFinancingInfo";
import OkpAffirmationInfo from "./OkpAffirmationInfo";
import OkpCollateralInfo from "./OkpCollateralInfo";
import OkpRemarksInfo from "./OkpRemarksInfo";
import UwdInfo from "./UwdInfo";

export type BankActions = {
  getBanksForDetails: ({ id: number }) => Promise<*>,
  getBanks: (text: string) => Promise<*>,
  createExcludedBank: ({ id: number, bankId: number }) => Promise<*>,
  deleteExcludedBank: ({ id: number, bankId: number }) => Promise<*>,
  updateBankSuggestionContactPerson: ({
    id: number,
    bankId: number,
    first_name: string,
    last_name: string
  }) => Promise<*>,
  updateUsedBanksContactPerson: ({
    id: number,
    bankId: number,
    first_name: string,
    last_name: string
  }) => Promise<*>,
  updateInquiry: ({ id: number, details: any }) => Promise<*>
};

type DetailsProps = {
  match: {
    params: {
      id: number
    }
  },
  getTabs: boolean => Array<{
    path: string,
    label: string,
    icon: string
  }>,
  backPath: boolean => string,
  getInquiry: ({ id: number }) => Promise<*>,
  site: "buba" | "bank" | "customer",
  renderPrepended: ({ data: any, requestFetch: () => void }) => React.Node,
  showStatus?: boolean,
  bankActions: BankActions,
  generatePDFtemplate?: () => void,
  downloadPdf: () => void
};

const fieldsToOmitInPayload = [[FEDERAL_STATE], [BANK_CODE], [BANK_NAME], [BANK_ADVISOR]];

const detailsWithoutNull = values => omitBy(omit(values, fieldsToOmitInPayload), isNull);

const resetRealEstateFields = (changedFields, values) => ({
  ...(changedFields[REAL_ESTATE_USAGE_KIND] &&
    [USE_TYPE__OWN_USE, USE_TYPE__OTHER].includes(values[REAL_ESTATE_USAGE_KIND]) && {
      [REAL_ESTATE_RENTED_OUT_AREA]: null,
      [REAL_ESTATE_COLD_RENT]: null,
      [REAL_ESTATE_TAXATION]: null
    }),
  ...(changedFields[REAL_ESTATE_HERITABLE_BUILDING_RIGHT] &&
    !values[REAL_ESTATE_HERITABLE_BUILDING_RIGHT] && {
      [REAL_ESTATE_HERITABLE_BUILDING_RIGHT_OBLIGATION]: null
    })
});

const Details = ({
  match: {
    params: { id }
  },
  getTabs,
  backPath,
  getInquiry,
  bankActions,
  site,
  renderPrepended = () => null,
  showStatus,
  generatePDFtemplate
}: DetailsProps) => {
  const [isBankDataLoading, setIsBankDataLoading] = React.useState(false);
  const [bankData, setBankData] = React.useState(null);

  return (
    <FlashMessageConsumer>
      {({ addMessage }) => (
        <DataFetcher
          apiAction={getInquiry}
          mapper={([
            inquiryResponse,
            companyResponse,
            additionalInfo,
            lastActionRequestResponse
          ]) => ({
            companyInfo: denormalizeJsonApi(companyResponse),
            inquiryDetails: denormalizeJsonApi(inquiryResponse),
            additionalInfo,
            lastActionRequest: denormalizeJsonApi(lastActionRequestResponse)
          })}
          initialPayload={{ id: Number(id) }}
          redirectOnError
        >
          {({ data: detailsData, isLoading, requestFetch }) => {
            const data = { ...initialResponse, ...detailsData };
            const { partnerName } = data.inquiryDetails;
            const initialInquiryDetails = {
              ...generateInitialValues(data.inquiryDetails.details),
              [FEDERAL_STATE]: data.inquiryDetails.guaranteeBank
                ? data.inquiryDetails.guaranteeBank.state
                : ""
            };
            const initialCompanyInfo = generateInitialValues(data.companyInfo);
            const isEstablishedEntrepreneur: boolean =
              initialInquiryDetails[COMPANY__ROLE] === COMPANY_ROLE__ESTABLISHED_ENTREPRENEUR;
            const inquiryType = data.inquiryDetails.creator.type;
            const isBankAdvisorInquiry = inquiryType === "bankAdvisor";
            const isOkpInquiry =
              (site === "bank" || site === "buba") && partnerName === OKP_PARTNER_NAME;
            const {
              additionalInfo: { inquiryCreator, createdAt, bankCode, bankName }
            } = data;
            const onError = status => {
              addMessage(statusErrors(status), "failure");
            };

            const handleSubmit = (values: {}, form) => {
              if (!form) {
                return;
              }
              const changedFields = form.getState().dirtyFields;
              if (changedFields && !isEmpty(changedFields)) {
                const details = {
                  ...detailsWithoutNull(values),
                  ...resetRealEstateFields(changedFields, values)
                };

                bankActions
                  .updateInquiry({
                    id: data.inquiryDetails.id,
                    details
                  })
                  .then(requestFetch)
                  .catch(err => {
                    onError(err.response && err.response.status);
                    requestFetch();
                  });
              }
            };

            return (
              <div className={formStyles.FIELD_STYLE_SUMMARY}>
                <InquiryPage
                  companyName={data.companyInfo.name}
                  issuedOn={new Date(createdAt)}
                  isLoadingData={isLoading || isBankDataLoading}
                  backPath={backPath(inquiryType)}
                  tabs={getTabs(inquiryType)}
                  site={site}
                  {...(showStatus ? { status: data.inquiryDetails.status } : {})}
                  generatePDFtemplate={generatePDFtemplate && generatePDFtemplate(inquiryType)}
                  pdfData={{ ...data, bankData }}
                >
                  {renderPrepended({ data, requestFetch })}

                  <UwdInfo
                    initialValues={{
                      [UWD_CHAMBER]: initialInquiryDetails.uwd?.chamber,
                      [UWD_CHAMBER_EXPERT]: initialInquiryDetails.uwd?.chamberExpert,
                      [UWD_CHAMBER_PROJECT]: initialInquiryDetails.uwd?.chamberProject
                    }}
                  />

                  {isOkpInquiry && (
                    <>
                      {site === "buba" && (
                        <PersonalInfo
                          numbered={false}
                          initialValues={{
                            ...generateInitialValues(inquiryCreator),
                            [BANK_CODE]: bankCode,
                            [BANK_NAME]: bankName,
                            [BANK_ADVISOR]: `${inquiryCreator.firstName} ${inquiryCreator.lastName}`
                          }}
                        />
                      )}

                      <OkpCompanyInfo
                        initialValues={{
                          [COMPANY__NAME]: initialCompanyInfo.name,
                          [COMPANY__STREET]: initialCompanyInfo.street,
                          [COMPANY__FOUNDING_DATE]: format(
                            initialCompanyInfo.founding_date,
                            "dd.MM.yyyy"
                          ),
                          [COMPANY__LOCATION]: `${initialCompanyInfo.zip_code} ${initialCompanyInfo.city}`,
                          [COMPANY__LEGAL_FORM]: initialCompanyInfo.legal_form
                        }}
                        numberedWithDot
                      />

                      <OkpProjectInfo
                        initialValues={{
                          [AMOUNT]: initialInquiryDetails.amount,
                          [COMPANY__ROLE]: initialInquiryDetails.company_role,
                          [PROJECT_START]: initialInquiryDetails.project_start,
                          [FEDERAL_STATE]: initialInquiryDetails.federal_state
                        }}
                        numberedWithDot
                      />

                      <OkpContactPersonInfo
                        initialValues={{
                          [COMPANY__CONTACT_PERSON_NAME]: initialCompanyInfo.contact_person
                            ? `${initialCompanyInfo.contact_person.firstName} ${initialCompanyInfo.contact_person.lastName}`
                            : null,
                          [COMPANY__CONTACT_PERSON_GENDER]:
                            initialCompanyInfo.contact_person?.gender,
                          [COMPANY__CONTACT_PERSON_PRIVACY_POLICY]:
                            initialInquiryDetails.privacy_policy_consent,
                          [COMPANY__CONTACT_PERSON_EMAIL]: initialCompanyInfo.contact_person?.email,
                          [COMPANY__CONTACT_PERSON_PHONE_NUMBER]:
                            initialCompanyInfo.contact_person?.phoneNumber,
                          [COMPANY__CONTACT_PERSON_COMMUNICATION_ONLY_VIA_BANK_ADVISOR]:
                            initialInquiryDetails?.communication_only_via_bank_advisor_consent
                        }}
                        numberedWithDot
                      />

                      <OkpFinancingInfo
                        initialValues={{
                          [PURPOSE_KIND]: initialInquiryDetails.use_of_funds?.purposeKind,
                          [FINANCING_PRODUCT]: initialInquiryDetails.source_of_funds?.productKind,
                          [FINANCING_DESCRIPTION]: initialInquiryDetails.financing_description,
                          [FINANCING_AMOUNT]:
                            initialInquiryDetails.source_of_funds?.totalFinancingAmount,
                          [INVESTMENT_AMOUNT]:
                            initialInquiryDetails.use_of_funds?.totalInvestmentAmount
                        }}
                        numberedWithDot
                      />

                      <OkpGrantsInfo
                        initialValues={{
                          [INFORMATION_ON_GRANTS]: initialInquiryDetails[INFORMATION_ON_GRANTS]
                        }}
                        numberedWithDot
                      />

                      <OkpAffirmationInfo
                        initialValues={{
                          [IS_ALREADY_SELF_EMPLOYED]:
                            initialInquiryDetails[IS_ALREADY_SELF_EMPLOYED],
                          [IS_BANKRUPT]: initialInquiryDetails[IS_BANKRUPT],
                          [IS_SME]: initialInquiryDetails[IS_SME],
                          [HAS_SHARES_IN_OTHER_COMPANY]:
                            initialInquiryDetails[HAS_SHARES_IN_OTHER_COMPANY]
                        }}
                        numberedWithDot
                      />

                      <OkpCollateralInfo
                        initialValues={{
                          [COLLATERALS]: initialInquiryDetails[COLLATERALS]
                        }}
                        numberedWithDot
                      />

                      <OkpRemarksInfo
                        initialValues={{
                          [REMARK]: initialInquiryDetails[REMARK]
                        }}
                      />
                    </>
                  )}

                  {!isOkpInquiry &&
                    (isBankAdvisorInquiry ? (
                      <>
                        {site === "buba" && (
                          <PersonalInfo
                            initialValues={{
                              ...generateInitialValues(inquiryCreator),
                              [BANK_CODE]: bankCode,
                              [BANK_NAME]: bankName,
                              [BANK_ADVISOR]: `${inquiryCreator.firstName} ${inquiryCreator.lastName}`
                            }}
                          />
                        )}
                        <Company
                          initialValues={{
                            ...initialCompanyInfo,
                            ...initialInquiryDetails
                          }}
                          type={inquiryType}
                          isBankAdvisorInquiry={isBankAdvisorInquiry}
                          site={site}
                        />
                        <InformationBankAdvisor initialValues={initialInquiryDetails} />
                      </>
                    ) : (
                      <>
                        <Summary
                          initialValues={initialInquiryDetails}
                          isEstablishedEntrepreneur={isEstablishedEntrepreneur}
                          site={site}
                        />
                        {site !== "customer" && <Creator initialValues={inquiryCreator} />}
                        <Company
                          initialValues={{
                            ...initialCompanyInfo,
                            ...initialInquiryDetails
                          }}
                          type={inquiryType}
                          isBankAdvisorInquiry={isBankAdvisorInquiry}
                          site={site}
                        />
                        <Investment
                          initialValues={initialInquiryDetails}
                          handleSubmit={handleSubmit}
                          isEstablishedEntrepreneur={isEstablishedEntrepreneur}
                          site={site}
                          type={inquiryType}
                        />
                        <Financing
                          initialValues={initialInquiryDetails}
                          handleSubmit={handleSubmit}
                          isEstablishedEntrepreneur={isEstablishedEntrepreneur}
                          site={site}
                          type={inquiryType}
                        />
                        {["buba", "customer"].includes(site) && !isLoading && (
                          <Bank
                            initialValues={initialInquiryDetails}
                            bankActions={bankActions}
                            handleSubmit={handleSubmit}
                            inquiryId={Number(data.inquiryDetails.id)}
                            isDetailsLoading
                            onError={onError}
                            setIsBankDataLoading={setIsBankDataLoading}
                            setBankData={setBankData}
                            site={site}
                          />
                        )}
                        <FurtherInformation
                          initialValues={initialInquiryDetails}
                          site={site}
                          handleSubmit={handleSubmit}
                        />
                      </>
                    ))}
                </InquiryPage>
              </div>
            );
          }}
        </DataFetcher>
      )}
    </FlashMessageConsumer>
  );
};

export default withRouter(Details);
